import {
  NewOrderImpressionsMode,
  TNewOrderCountry,
  TViewsKeyword,
} from "@/entities/new-order";
import { TReviewValue } from "@/entities/new-order/types/new-order-keywords.type";
import {
  getDatesListFromCountry,
  getKeysFromCountry,
} from "@/entities/new-order/utils";
import { TImpressionsInfo } from "@/Pages/CreateCampaignPage/Models/CountryModel";

/**
 * Очищает keywords от служебных полей (impressions, CR)
 */
export const cleanCountryKeywords = (
  countriesList: TNewOrderCountry[],
  appStore: string,
): TNewOrderCountry[] => {
  return countriesList.map((country) => {
    const newCountry = JSON.parse(JSON.stringify(country));

    if (
      Object.values(newCountry.keywords).some((value) => Array.isArray(value))
    ) {
      Object.entries(newCountry.keywords).forEach((item) => {
        const date = item[0];
        const reviews = item[1] as TReviewValue[];

        const fourStarReview = reviews
          .filter((review) => review.rating === 4)
          .map((review) =>
            appStore === "apple"
              ? { review: review.review, title: review.title }
              : { review: review.review },
          );
        const fiveStarReview = reviews
          .filter((review) => review.rating === 5)
          .map((review) =>
            appStore === "apple"
              ? { review: review.review, title: review.title }
              : { review: review.review },
          );

        const formattedReviews = {
          "4 star rates": fourStarReview,
          "5 star rates": fiveStarReview,
        };

        newCountry.keywords[date] = formattedReviews;
      });
    }

    // Удаляем impressions из корня страны
    if ("impressions" in newCountry) {
      delete newCountry.impressions;
    }

    // Удаляем CR из keywords
    const dates = getDatesListFromCountry(newCountry);
    const keys = getKeysFromCountry(newCountry);

    // Проверяем, что есть даты и ключи
    if (dates.length === 0 || keys.length === 0) {
      return newCountry;
    }

    dates.forEach((date) => {
      // Проверяем, что дата существует в keywords
      if (!newCountry.keywords[date]) {
        return;
      }

      keys.forEach((key) => {
        const keywordValue = newCountry.keywords[date][key];

        if (typeof keywordValue === "object" && keywordValue !== null) {
          const keyword: TViewsKeyword =
            keywordValue as unknown as TViewsKeyword;
          delete keyword.CR;
        }
      });
    });

    return newCountry;
  });
};

/**
 * Формирует информацию об impressions для типа install_with_views
 */
export const buildImpressionsInfo = (
  countriesList: TNewOrderCountry[],
): TImpressionsInfo => {
  const impressionsInfo: TImpressionsInfo = {};

  countriesList.forEach((country) => {
    const { geo, impressions, keywords } = country;

    if (!geo || !impressions) {
      return;
    }

    // Проверяем, что keywords существует
    if (!keywords || typeof keywords !== "object") {
      return;
    }

    impressionsInfo[geo] = {
      type: impressions.type,
      keys: {},
    };

    const dates = getDatesListFromCountry(country);
    const keys = getKeysFromCountry(country);

    // Проверяем, что есть даты и ключи
    if (dates.length === 0 || keys.length === 0) {
      return;
    }

    const firstDate = dates[0];

    // Проверяем, что первая дата существует в keywords
    if (!keywords[firstDate]) {
      return;
    }

    keys.forEach((key) => {
      const firstDateKeywords = keywords[firstDate];

      if (Array.isArray(firstDateKeywords)) {
        return;
      }

      const keyword = firstDateKeywords[key];

      // Проверяем, что keyword существует
      if (!keyword) {
        return;
      }

      if (
        typeof keyword === "object" &&
        "CR" in keyword &&
        impressions.type === NewOrderImpressionsMode.CR
      ) {
        impressionsInfo[geo].keys[key] = keyword.CR;
      } else if (
        typeof keyword === "object" &&
        "views" in keyword &&
        impressions.type === NewOrderImpressionsMode.Impressions
      ) {
        impressionsInfo[geo].keys[key] = keyword.views;
      }
    });
  });

  return impressionsInfo;
};

/**
 * Форматирует время отложенного старта для API
 */
export const formatDelayedStart = (
  delayedStartValue: string | null,
): string | null => {
  if (!delayedStartValue) {
    return null;
  }

  return `${delayedStartValue.slice(0, 5)} +00:00`;
  // return `${delayedStartValue.slice(0, -3)} +00:00`;
};
